import React, { useState } from "react";
import { dms } from "./api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RotateCcw } from "lucide-react";

// Reusable "Raise Return" button + modal.
// props:
//   level: "primary" | "secondary"
//   order: the order object (must have .items[] with product_id, product_name, sku_code)
//   retailerId: required for secondary level when raised by distributor
//   onDone: callback after successful submission
export function ReturnRequestButton({ level, order, retailerId, onDone }) {
  const [open, setOpen] = useState(false);
  const [qtyBoxes, setQtyBoxes] = useState({});
  const [qtyPcs, setQtyPcs] = useState({});
  const [reasons, setReasons] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const items = order?.items || [];

  const bq = (it) => (Number(it.box_qty) > 0 ? Number(it.box_qty) : null);
  const maxBoxesOf = (it) => {
    if (level !== "primary") return null;
    const v = it.qty_boxes_fulfilled != null ? it.qty_boxes_fulfilled : it.qty_boxes_ordered;
    return v == null ? null : Number(v);
  };
  const receivedLabel = (it) => {
    const mx = maxBoxesOf(it);
    if (mx == null) return "";
    return bq(it) ? `${Math.round(mx * bq(it))} pcs` : `${mx} boxes`;
  };
  const boxEquiv = (it) =>
    Number(qtyBoxes[it.product_id] || 0) + (bq(it) ? Number(qtyPcs[it.product_id] || 0) / bq(it) : 0);
  const pickedItems = () => items.filter((it) => Number(qtyBoxes[it.product_id] || 0) > 0 || Number(qtyPcs[it.product_id] || 0) > 0);

  const review = () => {
    const picked = pickedItems();
    if (picked.length === 0) {
      toast.error("Enter a return quantity for at least one item");
      return;
    }
    for (const it of picked) {
      const mx = maxBoxesOf(it);
      if (mx != null && boxEquiv(it) > mx + 0.0001) {
        toast.error(`${it.product_name}: return quantity is more than received (${receivedLabel(it)})`);
        return;
      }
    }
    setOpen(false);
    setConfirmOpen(true);
  };

  const submit = async () => {
    const outItems = items
      .map((it) => ({
        product_id: it.product_id,
        qty_boxes: Number(qtyBoxes[it.product_id] || 0),
        qty_pcs: Number(qtyPcs[it.product_id] || 0),
        reason: reasons[it.product_id] || "",
      }))
      .filter((it) => it.qty_boxes > 0 || it.qty_pcs > 0);
    if (outItems.length === 0) {
      toast.error("Enter a return quantity for at least one item");
      return;
    }
    setBusy(true);
    try {
      await dms.createReturn({
        level,
        order_id: order.id,
        order_no: order.order_no,
        retailer_id: retailerId,
        items: outItems,
        notes,
      });
      toast.success("Return request submitted");
      setConfirmOpen(false);
      setOpen(false);
      setQtyBoxes({}); setQtyPcs({}); setReasons({}); setNotes("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit return");
    }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-slate-300" data-testid="raise-return-btn">
        <RotateCcw size={14} className="mr-1" /> Return
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Raise Return — {order?.order_no}</DialogTitle></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-3">
            {items.map((it) => (
              <div key={it.product_id} className="grid grid-cols-12 gap-2 items-end border-b border-slate-100 pb-2">
                <div className="col-span-4">
                  <div className="font-medium text-sm text-slate-900">{it.product_name}</div>
                  <div className="text-[10px] font-mono text-slate-500">{it.sku_code}</div>
                  {maxBoxesOf(it) != null && <div className="text-[11px] text-emerald-700 mt-0.5">Received: {receivedLabel(it)}</div>}
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Boxes</Label>
                  <Input type="number" min={0} step="0.1"
                    value={qtyBoxes[it.product_id] || ""}
                    onChange={(e) => setQtyBoxes({ ...qtyBoxes, [it.product_id]: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Pieces</Label>
                  <Input type="number" min={0}
                    value={qtyPcs[it.product_id] || ""}
                    onChange={(e) => setQtyPcs({ ...qtyPcs, [it.product_id]: e.target.value })} />
                </div>
                <div className="col-span-4">
                  <Label className="text-xs">Reason</Label>
                  <Input value={reasons[it.product_id] || ""}
                    onChange={(e) => setReasons({ ...reasons, [it.product_id]: e.target.value })}
                    placeholder="damaged / wrong item / etc" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2">
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={review} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white">
              Review Return
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Are you sure you want to return this order?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">{order?.order_no} — only these items will be returned:</div>
          <div className="max-h-[40vh] overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
            {pickedItems().map((it) => (
              <div key={it.product_id} className="flex items-start justify-between gap-3 p-3 text-sm">
                <div>
                  <div className="font-medium text-slate-900">{it.product_name}</div>
                  {reasons[it.product_id] && <div className="text-xs text-slate-500">Reason: {reasons[it.product_id]}</div>}
                </div>
                <div className="text-right font-semibold text-slate-900 whitespace-nowrap">
                  {Number(qtyBoxes[it.product_id] || 0) > 0 && <div>{Number(qtyBoxes[it.product_id])} boxes</div>}
                  {Number(qtyPcs[it.product_id] || 0) > 0 && <div>{Number(qtyPcs[it.product_id])} pcs</div>}
                </div>
              </div>
            ))}
          </div>
          {notes && <div className="text-xs text-slate-500">Notes: {notes}</div>}
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setOpen(true); }}>Back</Button>
            <Button onClick={submit} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="confirm-return-btn">
              Yes, Return Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
